import React, { useState, useEffect, useCallback } from "react";
import "./SideFilter.css";
import { getTagList, getSubCategoryByCategory, getBrandsByCategory } from "../../redux/services/productService";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material"
const SideFilter = ({
  isOpen,
  onClose,
  selectedCategoryId,
  selectedSubCategoryIds,
  selectedBrandIds,
  onCategorySelect,        // existing: called with the selected node
  onMainCategorySelect,    // optional: will be called with the top-level category containing the selected node
  onSubCategoryToggle,
  onBrandToggle,
  onPriceRangeChange,
  onStockStatusChange,
  resetFilters,
  IsLoad1stItem,
  setdefaultCategoryLoaded
}) => {
  const [expandedCategories, setExpandedCategories] = useState({}); // map id => boolean
  const [mainCategories, setMainCategories] = useState([]); // top-level categories from getTagList(3004)
  const [categories, setCategories] = useState([]); // tree (we attach children into this)
  const [brands, setBrands] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [activeThumb, setActiveThumb] = useState(null);

  // Price range
  const [priceRange, setPriceRange] = useState([0, 741362]);
  const [maxPrice] = useState(741362);

  useEffect(() => {
  // when resetFilters toggles, reset the price range
  setPriceRange([0, 741362]); // default min/max
  setInStockOnly(false)
}, [resetFilters]);

  // Helper: convert API subcategory objects to our nested shape (recursive)
  const convertApiSubcats = useCallback((items) => {
    if (!Array.isArray(items)) return [];
    return items.map((it) => ({
      Id: it.Id,
      Name: it.Name,
      ProductCount: it.ProductCount ?? 0,
      // Keep original SubCategories key mapping to our subcategories property
      subcategories: convertApiSubcats(it.SubCategories || [])
    }));
  }, []);

  const parseSubcategoriesFromResponse = useCallback((res) => {
    try {
      if (!res) return [];
      const parsed1 = typeof res.result === "string" ? JSON.parse(res.result) : res.result;
      const maybeData = parsed1?.[0]?.data;
      if (!maybeData) return [];
      const parsed2 = typeof maybeData === "string" ? JSON.parse(maybeData) : maybeData;
      const subcats = parsed2 || [];
      return convertApiSubcats(subcats);
    } catch (err) {
      console.error("Failed to parse subcategories response", err);
      return [];
    }
  }, [convertApiSubcats]);


  // Fetch brands based on selected category
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        let res;
        if (!selectedCategoryId) {
          res = await getBrandsByCategory();
        } else {
          res = await getBrandsByCategory(selectedCategoryId);
        }
        const parsed = typeof res?.result === "string" ? JSON.parse(res.result) : res?.result;
        setBrands(parsed ?? []);
      } catch (error) {
        console.error("Error fetching brands:", error);
        setBrands([]);
      }
    };

    fetchBrands();
  }, [selectedCategoryId]);

  // Utility: find top-level category (from mainCategories) that contains nodeId in its subtree
  const findRootForNode = useCallback((nodeId) => {
    if (!Array.isArray(mainCategories) || mainCategories.length === 0) return null;

    const containsId = (item, id) => {
      if (!item) return false;
      if (item.Id === id) return true;
      if (!Array.isArray(item.subcategories)) return false;
      for (const c of item.subcategories) {
        if (containsId(c, id)) return true;
      }
      return false;
    };

    for (const root of mainCategories) {
      if (containsId(root, nodeId)) return root;
    }
    return null;
  }, [mainCategories]);

  // Fetch and attach subcategories returned from getSubCategoryByCategory into categories tree
useEffect(() => {
  const fetchAllCategories = async () => {
    try {
      const res = await getSubCategoryByCategory(0);
      const subcats = parseSubcategoriesFromResponse(res) || [];
     
      
      // This response already contains the full category tree
      setCategories(subcats);
      setMainCategories(subcats); // Top-level = main categories
       if(IsLoad1stItem){
       
        onCategorySelect(subcats[0])
       }
    } catch (error) {
      console.error("Error fetching full categories:", error);
      setCategories([]);
      setMainCategories([]);
    }
    finally{
      setdefaultCategoryLoaded(false)
    }
  };
  fetchAllCategories();
}, [parseSubcategoriesFromResponse]);

  // Toggle expand for a node (fetch children on-demand if needed).
  // Only call API when the node is a top-level main category; for other nodes, do not call API.
const toggleCategory = (node) => {
  setExpandedCategories(prev => ({
    ...prev,
    [node.Id]: !prev[node.Id]
  }));
};


  // When selecting a node: call onCategorySelect(node) and also call onMainCategorySelect(root) if provided
  const handleSelectNode = (node) => {
    if (onCategorySelect) onCategorySelect(node);

    // find top-level parent and call onMainCategorySelect if provided
    const root = findRootForNode(node.Id) || node;
    if (onMainCategorySelect) onMainCategorySelect(root);
  };

  // ✅ Fixed price range handlers
  const handlePriceChange = (index, value) => {
    const numValue = Number(value);
    
    setPriceRange(prev => {
      const updated = [...prev];
      
      if (index === 0) {
        // Min thumb - ensure it doesn't exceed max value
        updated[0] = Math.min(numValue, prev[1] - 1);
      } else {
        // Max thumb - ensure it doesn't go below min value
        updated[1] = Math.max(numValue, prev[0] + 1);
      }
      
      // Ensure values stay within bounds
      updated[0] = Math.max(0, Math.min(updated[0], maxPrice - 1));
      updated[1] = Math.max(1, Math.min(updated[1], maxPrice));
      
      return updated;
    });
  };

  const applyPriceFilter = () => {
    if (onPriceRangeChange) {
      onPriceRangeChange(priceRange);
    }
  };

  const handleStockStatusToggle = (newInStockOnly) => {
    setInStockOnly(newInStockOnly);
    if (onStockStatusChange) onStockStatusChange(newInStockOnly);
  };

  // Recursive tree renderer
  const TreeNode = ({ node, depth = 0 }) => {
    const hasChildren = Array.isArray(node.subcategories) && node.subcategories.length > 0;
    const isExpanded = !!expandedCategories[node.Id];

    // Determine if this node is a top-level main category
    const isMainCategory = Array.isArray(mainCategories) && mainCategories.some((c) => c.Id === node.Id);

    // Show toggle only for:
    // - main category nodes (even if children are not loaded yet), OR
    // - nodes that already have loaded subcategories
    const showToggle = hasChildren;

    const onToggle = (e) => {
      e.stopPropagation();
      toggleCategory(node);
    };

    const onRadioChange = (e) => {
      e.stopPropagation();
      handleSelectNode(node);
    };

    return (
      <div className="tree-node" style={{ paddingLeft: depth * 18 + "px" }}>
        <div className="tree-node-row">
          <label
            className="tree-node-label"
            onClick={(e) => {
              e.stopPropagation();
              handleSelectNode(node);
            }}
          >
            <input
              className="category-radio"
              type="radio"
              name="category-tree"
              checked={selectedCategoryId === node.Id}
              onChange={onRadioChange}
            />
            <span className="node-name" style={{fontSize:"11px"}}>{node.Name}</span>
          </label>

          <div className="node-right">
            <span className="node-count" style={{fontSize:"12px"}}>{node.ProductCount ?? 0}</span>

            {showToggle ? (
              // Use angle-bracket style requested by user: display "<" for collapsed and ">" for expanded.
              // Render as string to avoid JSX fragment parsing.
             <button
  className={`tree-toggle ${isExpanded ? "expanded" : ""}`}
  onClick={onToggle}
  aria-label={isExpanded ? "Collapse" : "Expand"}
>
  {isExpanded ? (
  <KeyboardArrowUp sx={{ fontSize: 13 }} />  // or style={{ fontSize: '16px' }}
) : (
  <KeyboardArrowDown sx={{ fontSize: 13 }} />
)}
</button>
            ) : (
  <span style={{ display: "block", width: "20px" }}></span>
)}
          </div>
        </div>

        {isExpanded && hasChildren && (
          <div className="tree-children">
            {node.subcategories.map((child) => (
              <TreeNode key={child.Id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleSubCategoryToggleLocal = (sub) => {
    if (onSubCategoryToggle) onSubCategoryToggle(sub);
  };

  const handleBrandToggleLocal = (brand) => {
    if (onBrandToggle) onBrandToggle(brand);
  };
  const getThumbZIndex = (index, isActive = false) => {
  const [min, max] = priceRange;
  
  if (isActive) return 4; // Active thumb always on top
  
  if (min === max) {
    // Thumbs coinciding
    if (min === 0) return index === 0 ? 3 : 2; // Left edge - left thumb on top
    if (max === maxPrice) return index === 0 ? 2 : 3; // Right edge - right thumb on top
    return index === 0 ? 3 : 2; // Middle - left thumb on top by default
  }
  
  return index === 0 ? 3 : 2; // Normal case - left thumb above right
};

  return (
    <div className={`side-filter ${isOpen ? "mobile-open" : ""}`}>
      <div className="stock-status">
        <h3 className="filter-title">STOCK STATUS</h3>
        <div style={{display:"flex",flexDirection:"row",gap:2}}>
        <button
          className={`stock-btn ${inStockOnly ? "":  "active"}`}
          onClick={()=>handleStockStatusToggle(false)}
        >
           All Products
        </button>
        <button
          className={`stock-btn ${inStockOnly ? "active" : ""}`}
          onClick={()=>handleStockStatusToggle(true)}
        >
         In Stock
        </button>
        </div>
      </div>

      <div className="side-filter-categories-section">
        <h3 className="side-filter-section-title">PRODUCT CATEGORIES</h3>
        <div className="side-filter-categories-content side-filter-scrollable">
          <div className="side-filter-categories-list">
            {categories.map((category) => (
              <div key={category.Id} className="side-filter-category-item">
                <TreeNode node={category} depth={0} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="side-filter-brands-section">
        <h3 className="side-filter-section-title">BRANDS</h3>
        <div className="side-filter-brands-content side-filter-scrollable">
          <div className="side-filter-brands-list">
            {brands.map((brand) => (
              <label
                key={brand.BrandId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "12px",      // smaller label text
                  gap: "4px",            // small space between checkbox and text
                  lineHeight: 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedBrandIds.includes(brand.BrandId)}
                  onChange={() => handleBrandToggleLocal(brand)}
                  style={{
                    width: "12px",   // smaller checkbox
                    marginRight: "4px",
                  }}
                />
                <span>{brand.BrandName}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* ✅ Updated Price Range Section */}
      
      <div className="price-range-container-Shop">
        <div className="slider-values-Shop">
          <span className="min-val">AED {priceRange[0]}</span>
          <span className="max-val">AED {priceRange[1]}</span>
        </div>

        <div className="range-slider-container-Shop">
          <div
            className="range-track-Shop"
            style={{
              left: `${(priceRange[0] / maxPrice) * 100}%`,
              width: `${((priceRange[1] - priceRange[0]) / maxPrice) * 100}%`,
            }}
          ></div>

          <input
            type="range"
            min="0"
            max={maxPrice}
            value={priceRange[0]}
            onChange={(e) => handlePriceChange(0, e.target.value)}
            className="range-thumb-Shop"
            onMouseDown={() => setActiveThumb(0)}
            onTouchStart={() => setActiveThumb(0)}
            style={{
              zIndex: getThumbZIndex(0, activeThumb === 0)
            }}
          />
          <input
            type="range"
            min="0"
            max={maxPrice}
            value={priceRange[1]}
            onChange={(e) => handlePriceChange(1, e.target.value)}
            className="range-thumb-Shop"
            onMouseDown={() => setActiveThumb(1)}
            onTouchStart={() => setActiveThumb(1)}
            style={{
              zIndex: getThumbZIndex(1, activeThumb === 1)
            }}
          />
        </div>

        <div className="price-inputs-Shop">
          <span>AED</span>
          <input
            style={{fontSize:"12px"}}
            type="number"
            value={priceRange[0]}
            onChange={(e) => handlePriceChange(0, e.target.value)}
          />
          <span>-</span>
          <input
            style={{fontSize:"12px"}}
            type="number"
            value={priceRange[1]}
            onChange={(e) => handlePriceChange(1, e.target.value)}
          />
        </div>

        <button className="filter-btn" onClick={applyPriceFilter}>
          FILTER
        </button>
      </div>
    </div>
  );
};

export default SideFilter;